import React from "react";
import cx from "classnames";
import { DateTime } from "luxon";
import { useStaticQuery, graphql } from "gatsby";

import Location from "~/views/identity/location";
import Organizer from "~/views/identity/organizer";
import Event from "~/models/event";

type Props = {
  className?: string;
};

function NewEvents({ className }: Props) {
  const { allEventYaml }: any = useStaticQuery(
    graphql`
      query NewEvents {
        allEventYaml(sort: { sys: { createdAt: DESC } }, limit: 5) {
          nodes {
            id
            title
            organizer
            startAt
            createdAt
            endAt
            venue
            url
            time_tbd
            image {
              imgix {
                url(imgixParams: { w: 320, dpr: 2 })
              }
            }
          }
        }
      }
    `
  );

  return (
    <div className={cx(className)}>
      <p className="font-extrabold text-4xl text-neutral-90 mb-6">
        Newly Added Events
      </p>

      <ul>
        {allEventYaml.nodes
          .map((event: any) => new Event(event))
          .filter((event: Event) => event.endAt > DateTime.now())
          .map((event: Event) => (
            <li key={event.id} className="mb-12 last:mb-0">
              <EventCard event={event} />
            </li>
          ))}
      </ul>
    </div>
  );
}

export default NewEvents;

type CardProps = {
  event: Event;
  className?: string;
};

function EventCard({ event, className }: CardProps) {
  return (
    <div className={cx(className, "bg-neutral-99 rounded-xl px-2 pt-2 pb-4")}>
      <a
        aria-label={event.title}
        href={event.url}
        rel="noopener noreferrer"
        target="_blank"
        className="group block relative rounded-lg mb-4 overflow-hidden isolate">
        <img
          src={event.image.imgix?.url}
          alt={event.title}
          loading="eager"
          className="overflow-hidden transition-all ease-in-out duration-500 group-hover:opacity-[0.85] group-hover:scale-[1.005]"
        />
      </a>
      <div className="px-1">
        <div className="text-3xs font-semibold mb-1">{event.dateLabel}</div>

        <a
          aria-label={event.title}
          href={event.url}
          rel="noopener noreferrer"
          target="_blank"
          className="block text-sm font-bold leading-[1.4] mb-2 hover:underlined-link">
          {event.title}
        </a>

        <div className="flex gap-1 items-center mb-1">
          <Organizer />
          <span className="text-4xs text-neutral-60 font-medium">
            {event.organizer}
          </span>
        </div>

        {event.venue && (
          <div className="flex gap-1 items-center">
            <Location />
            <span className="text-4xs text-neutral-60 font-medium">
              {event.venue}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
